import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const CUSTOM_INSTRUCTIONS_KEY = 'capitol-agent-chat-custom-instructions';

export function loadAgentChatCustomInstructions(): string {
  try {
    return localStorage.getItem(CUSTOM_INSTRUCTIONS_KEY) || '';
  } catch {
    return '';
  }
}

export function persistAgentChatCustomInstructions(value: string) {
  try {
    if (value) {
      localStorage.setItem(CUSTOM_INSTRUCTIONS_KEY, value);
    } else {
      localStorage.removeItem(CUSTOM_INSTRUCTIONS_KEY);
    }
  } catch {
    /* ignore */
  }
}

export const CUSTOM_INSTRUCTIONS_RE =
  /^\[CUSTOM INSTRUCTIONS\]\r?\n([\s\S]*?)\r?\n\[\/CUSTOM INSTRUCTIONS\]\r?\n\r?\n/;

export function wrapWithCustomInstructions(
  message: string,
  instructions: string,
): string {
  if (!instructions) return message;
  return `[CUSTOM INSTRUCTIONS]\n${instructions}\n[/CUSTOM INSTRUCTIONS]\n\n${message}`;
}

export function stripCustomInstructions(content: string): {
  text: string;
  instructions?: string;
} {
  const match = content.match(CUSTOM_INSTRUCTIONS_RE);
  if (!match) return { text: content };
  return {
    text: content.replace(CUSTOM_INSTRUCTIONS_RE, ''),
    instructions: match[1].trim(),
  };
}

interface CustomInstructionsFormProps {
  customInstructions: string;
  onSave: (value: string) => void;
  /** Called after Save or Clear so the hosting popover can close. */
  onDone: () => void;
}

/**
 * The popover body (title, helper text, textarea, Clear/Save) the prompt
 * bar's "+" menu opens; the chat-settings gear keeps its own section since
 * it also hosts the clarification preferences.
 */
export function CustomInstructionsForm({
  customInstructions,
  onSave,
  onDone,
}: CustomInstructionsFormProps) {
  const [draft, setDraft] = useState(customInstructions);

  return (
    <div className='space-y-3'>
      <div>
        <Label
          htmlFor='agent-chat-custom-instructions-form'
          className='text-sm font-medium'
        >
          Custom Instructions
        </Label>
        <p className='text-xs text-muted-foreground mt-0.5'>
          These instructions are included with every message you send to the
          agent.
        </p>
      </div>
      <Textarea
        id='agent-chat-custom-instructions-form'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder='e.g., Keep answers short. Prefer tables for comparisons.'
        rows={5}
        className='text-sm resize-none'
      />
      <div className='flex justify-between'>
        <Button
          variant='ghost'
          size='sm'
          onClick={() => {
            setDraft('');
            onSave('');
            onDone();
          }}
          disabled={!customInstructions && !draft}
        >
          Clear
        </Button>
        <Button
          size='sm'
          onClick={() => {
            onSave(draft);
            onDone();
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
